import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  collection, doc, getDocs, onSnapshot, query, setDoc, updateDoc, where,
} from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db } from '../firebase';
import { Heading } from '../components/heading';
import { Text } from '../components/text';
import { useUser } from '../components/UserContext';
import { logBitacora } from '../utils/bitacora';
import {
  CANAL_META,
  CANAL_ORDEN,
  ROLES_ELEGIBLES,
  ZONAS,
  resolverConfig,
  estadoCelda,
  toggleCelda,
  zonaDetectada,
  cuartoDeOrg,
} from '../utils/notificaciones';
import { ExclamationTriangleIcon, PaperAirplaneIcon } from '@heroicons/react/24/outline';

const field =
  'rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none ' +
  'focus:border-azul-surelia focus:ring-1 focus:ring-azul-surelia';

const DIAS = [
  { value: 1, label: 'Lunes' },
  { value: 2, label: 'Martes' },
  { value: 3, label: 'Miércoles' },
  { value: 4, label: 'Jueves' },
  { value: 5, label: 'Viernes' },
  { value: 6, label: 'Sábado' },
  { value: 7, label: 'Domingo' },
];

const HORAS = Array.from({ length: 24 }, (_, h) => ({
  value: h,
  label: `${String(h).padStart(2, '0')}:00`,
}));

const ROL_LABEL = { owner: 'Administrador', operator: 'Operador' };

/**
 * Configuración → Notificaciones. Owner-only (gated in Configuracion.js, and
 * again by the rules: orgs/{org}/config/* is owner-write).
 *
 * This screen is LAYER 1 of two — it says who SHOULD get what. Each person can
 * still opt out of their own mail in Configuración → Correo, and that wins. The
 * grid shows an amber warning on any cell where the owner assigned something
 * the person has unsubscribed from; without it the screen would render a
 * checked box that silently never sends.
 *
 * Membership is read from `usuarios` by org, NOT from the legacy
 * `orgs.usuarios` array — same source the scheduler uses server-side.
 */
const ConfiguracionNotificaciones = () => {
  const { user, userData } = useUser();
  const org = userData?.org;

  const [stored, setStored] = useState(undefined); // undefined = loading
  const [miembros, setMiembros] = useState(null);
  // Test send: which uid is in flight, and the last outcome per uid.
  const [pruebaEnviando, setPruebaEnviando] = useState(null);
  const [pruebaResultado, setPruebaResultado] = useState({});
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!org) return undefined;
    const unsub = onSnapshot(
      doc(db, 'orgs', org, 'config', 'notificaciones'),
      (snap) => { setStored(snap.exists() ? snap.data() : null); setError(null); },
      (e) => { console.error('notificaciones config listener:', e); setStored(null); }
    );
    return () => unsub();
  }, [org]);

  useEffect(() => {
    if (!org) return;
    let cancelado = false;
    (async () => {
      try {
        const snap = await getDocs(query(collection(db, 'usuarios'), where('org', '==', org)));
        const list = snap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .filter((u) => ROLES_ELEGIBLES.includes(u.rol))
          .sort((a, b) => {
            // Owners first, then alphabetical — the owner reading this screen
            // wants to find themselves and their admins at the top.
            if ((a.rol === 'owner') !== (b.rol === 'owner')) return a.rol === 'owner' ? -1 : 1;
            return (a.nombre || a.correo || '').localeCompare(b.nombre || b.correo || '', 'es');
          });
        if (!cancelado) setMiembros(list);
      } catch (e) {
        console.error('Failed to load org members:', e);
        if (!cancelado) setMiembros([]);
      }
    })();
    return () => { cancelado = true; };
  }, [org]);

  const config = useMemo(() => resolverConfig(stored), [stored]);

  // First visit writes nothing until the owner touches something; the zone
  // select just shows what their machine reports so they rarely have to change
  // it. Once stored, the stored value wins.
  const zonaMostrada = stored && stored.zona ? config.zona : zonaDetectada();

  const escribir = useCallback(async (updates, descripcion) => {
    if (!org) return;
    setGuardando(true);
    setError(null);
    try {
      // setDoc+merge so the first write creates the doc; dotted paths in
      // `updates` need updateDoc, so branch on whether the doc exists yet.
      if (stored === null || stored === undefined) {
        await setDoc(doc(db, 'orgs', org, 'config', 'notificaciones'), {
          zona: zonaMostrada,
          hora: config.hora,
          diaSemanal: config.diaSemanal,
          miembros: config.miembros,
          nuevosApagados: config.nuevosApagados,
          sinMontos: config.sinMontos,
        }, { merge: true });
        await updateDoc(doc(db, 'orgs', org, 'config', 'notificaciones'), updates);
      } else {
        await updateDoc(doc(db, 'orgs', org, 'config', 'notificaciones'), updates);
      }
      if (descripcion) {
        await logBitacora({
          action: 'UPDATE',
          collection: 'orgs',
          documentId: org,
          user,
          userData,
          metadata: { description: descripcion },
        });
      }
    } catch (e) {
      console.error('guardar notificaciones failed', e);
      setError(e.message || 'No se pudo guardar.');
    } finally {
      setGuardando(false);
    }
  }, [org, stored, config, zonaMostrada, user, userData]);

  // Sends the weekly digest to one member right now, as the scheduler would
  // build it, with "[Prueba]" in the subject. Server-side it ignores the grid
  // and the opt-out and does not move the member's baseline.
  const enviarPrueba = async (u) => {
    setPruebaEnviando(u.id);
    setPruebaResultado((prev) => ({ ...prev, [u.id]: null }));
    try {
      const fn = httpsCallable(getFunctions(), 'enviarResumenPrueba');
      const res = await fn({ uid: u.id, canal: 'resumen_semanal' });
      setPruebaResultado((prev) => ({
        ...prev,
        [u.id]: { ok: true, texto: `Enviado a ${res.data?.to || u.correo}` },
      }));
    } catch (e) {
      setPruebaResultado((prev) => ({
        ...prev,
        [u.id]: { ok: false, texto: e.message || 'No se pudo enviar.' },
      }));
    } finally {
      setPruebaEnviando(null);
    }
  };

  if (stored === undefined || miembros === null) {
    return (
      <div className="flex items-center justify-center min-h-[300px]">
        <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-azul-surelia"></div>
      </div>
    );
  }

  const hayBajas = miembros.some((u) =>
    CANAL_ORDEN.some((c) => estadoCelda(config, u, c).bajaPersonal));

  return (
    <div className="space-y-6">
      <div>
        <Heading level={2} className="text-azul-surelia">Notificaciones</Heading>
        <Text className="text-sm text-zinc-600 mt-1">
          Qué recibe cada miembro de la organización
        </Text>
      </div>

      <div className="bg-white rounded-lg border border-zinc-200 shadow-sm p-4">
        <div className="flex flex-wrap items-end gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Zona</label>
            <select
              value={zonaMostrada}
              disabled={guardando}
              onChange={(e) => escribir(
                { zona: e.target.value },
                `Cambió la zona de notificaciones a ${e.target.value}`
              )}
              className={field}
            >
              {ZONAS.map((z) => (
                <option key={z.value} value={z.value}>{z.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Hora de envío</label>
            <select
              value={config.hora}
              disabled={guardando}
              onChange={(e) => escribir(
                { hora: Number(e.target.value) },
                `Cambió la hora de notificaciones a ${e.target.value}:00`
              )}
              className={field}
            >
              {HORAS.map((h) => (
                <option key={h.value} value={h.value}>{h.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Día del semanal</label>
            <select
              value={config.diaSemanal}
              disabled={guardando}
              onChange={(e) => escribir(
                { diaSemanal: Number(e.target.value) },
                `Cambió el día del resumen semanal`
              )}
              className={field}
            >
              {DIAS.map((d) => (
                <option key={d.value} value={d.value}>{d.label}</option>
              ))}
            </select>
          </div>
        </div>
        {/* The quarter is derived, not chosen — say so rather than letting the
            owner wonder why mail lands at :30. */}
        <Text className="text-xs text-zinc-400 mt-3">
          Los correos salen alrededor de las {String(config.hora).padStart(2, '0')}:
          {String(cuartoDeOrg(org)).padStart(2, '0')} en esta zona.
        </Text>
        <label className="mt-3 flex items-center gap-2 text-sm text-zinc-700 cursor-pointer">
          <input
            type="checkbox"
            checked={config.nuevosApagados}
            disabled={guardando}
            onChange={(e) => escribir(
              { nuevosApagados: e.target.checked },
              e.target.checked
                ? 'Los miembros sin configurar empiezan sin correos'
                : 'Los miembros sin configurar reciben los correos por default'
            )}
            className="rounded border-gray-300 text-azul-surelia focus:ring-azul-surelia"
          />
          Los miembros sin configurar empiezan sin correos
        </label>
        <label className="mt-2 flex items-center gap-2 text-sm text-zinc-700 cursor-pointer">
          <input
            type="checkbox"
            checked={config.sinMontos}
            disabled={guardando}
            onChange={(e) => escribir(
              { sinMontos: e.target.checked },
              e.target.checked
                ? 'Los correos de resumen salen sin montos'
                : 'Los correos de resumen incluyen montos'
            )}
            className="rounded border-gray-300 text-azul-surelia focus:ring-azul-surelia"
          />
          Sin montos en los correos
        </label>
      </div>

      {hayBajas && (
        <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5">
          <ExclamationTriangleIcon className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <Text className="text-sm text-amber-800">
            Hay miembros dados de baja de correos que les asignaste. No los van a
            recibir hasta que ellos los reactiven en Configuración → Correo.
          </Text>
        </div>
      )}

      <div className="bg-white rounded-lg border border-zinc-200 shadow-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-azul-surelia text-left text-xs font-medium text-white">
              <th className="px-4 py-3">Miembro</th>
              {CANAL_ORDEN.map((c) => (
                <th key={c} className="px-4 py-3 text-center whitespace-nowrap">
                  <div>{CANAL_META[c].label}</div>
                  <div className="font-normal text-zinc-400">{CANAL_META[c].detalle}</div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-100">
            {miembros.map((u) => (
              <tr key={u.id}>
                <td className="px-4 py-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="font-medium text-zinc-800">
                        {u.nombre || u.correo || u.id}
                      </div>
                      {u.nombre && u.correo && (
                        <div className="text-xs text-zinc-500">{u.correo}</div>
                      )}
                      <div className="text-xs text-zinc-400">
                        {ROL_LABEL[u.rol] || u.rol}
                        {u.id === user?.uid ? ' · tú' : ''}
                      </div>
                      {pruebaResultado[u.id] && (
                        <div className={`text-xs mt-0.5 ${
                          pruebaResultado[u.id].ok ? 'text-green-700' : 'text-red-700'
                        }`}>
                          {pruebaResultado[u.id].texto}
                        </div>
                      )}
                    </div>
                    <button
                      type="button"
                      title="Enviar prueba del resumen semanal"
                      onClick={() => enviarPrueba(u)}
                      disabled={!u.correo || pruebaEnviando !== null}
                      className="shrink-0 p-1.5 rounded text-zinc-400 hover:bg-zinc-100 hover:text-azul-surelia disabled:opacity-40 disabled:hover:bg-transparent"
                    >
                      <PaperAirplaneIcon className={`w-4 h-4 ${
                        pruebaEnviando === u.id ? 'animate-pulse' : ''
                      }`} />
                    </button>
                  </div>
                </td>
                {CANAL_ORDEN.map((c) => {
                  const est = estadoCelda(config, u, c);
                  if (!est.aplica) {
                    return (
                      <td key={c} className="px-4 py-3 text-center text-zinc-300">—</td>
                    );
                  }
                  return (
                    <td key={c} className="px-4 py-3 text-center">
                      <div className="inline-flex items-center gap-1.5">
                        <input
                          type="checkbox"
                          checked={est.asignado}
                          disabled={guardando}
                          onChange={(e) => escribir(
                            toggleCelda(u.id, c, e.target.checked),
                            `${e.target.checked ? 'Activó' : 'Desactivó'} ` +
                            `"${CANAL_META[c].label}" para ${u.nombre || u.correo || u.id}`
                          )}
                          className="rounded border-gray-300 text-azul-surelia focus:ring-azul-surelia"
                        />
                        {est.bajaPersonal && (
                          <span title="Se dio de baja de estos correos">
                            <ExclamationTriangleIcon className="w-4 h-4 text-amber-500" />
                          </span>
                        )}
                      </div>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {error && (
        <div className="rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-xs text-red-700">
          {error}
        </div>
      )}
    </div>
  );
};

export default ConfiguracionNotificaciones;
